import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  OverlayTrigger,
  ProgressBar,
  Modal,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import {
  ArrowDownRight,
  ArrowLeft,
  ArrowUpRight,
  ChatLeftText,
  ChevronDown,
  ChevronRight,
  InfoCircle,
  ExclamationCircle,
  GeoAlt,
  Shop,
  StarFill,
} from "react-bootstrap-icons";
import Header from "../../../layouts/Header";
import Footer from "../../../layouts/Footer";
import { useNavigate } from "react-router-dom";
import FeedbackDetailDrawer from "./FeedbackDetailDrawer";
import axiosInstance from "../../../api/axios";

const API_BASE_URL = "https://apisay.xpansecoffee.com/api";
const LATEST_FEEDBACK_LIMIT = 5;

const FeedbackListing = () => {
  const navigate = useNavigate();
  const filterRef = useRef(null);

  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const accessToken = localStorage.getItem("authToken");

  /* ---------------------------------------------------------------------- */
  /* FILTERS                                                                */
  /* ---------------------------------------------------------------------- */

  const [outlets, setOutlets] = useState([
    {
      value: "all",
      label: "All Outlets",
    },
  ]);

  const [selectedOutletId, setSelectedOutletId] = useState("all");
  const [showOutletMenu, setShowOutletMenu] = useState(false);

  const [dateRange, setDateRange] = useState("Last 30 Days");
  const [showRangeMenu, setShowRangeMenu] = useState(false);

  const [feedbackType, setFeedbackType] = useState("all");
  const [hasSuggestions, setHasSuggestions] = useState("all");

  const rangeOptions = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
  ];

  /* ---------------------------------------------------------------------- */
  /* DATA                                                                   */
  /* ---------------------------------------------------------------------- */

  const [kpiData, setKpiData] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);

  /* ---------------------------------------------------------------------- */
  /* DRAWER                                                                 */
  /* ---------------------------------------------------------------------- */

  const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [selectedRatingMetric, setSelectedRatingMetric] = useState(null);

  /* ---------------------------------------------------------------------- */
  /* LOAD STATES                                                            */
  /* ---------------------------------------------------------------------- */

  const [kpiLoading, setKpiLoading] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [error, setError] = useState("");

  /* ---------------------------------------------------------------------- */
  /* HELPERS                                                                */
  /* ---------------------------------------------------------------------- */

  const getHeaders = () => {
    if (!accessToken) return {};

    return {
      Authorization: `Bearer ${accessToken}`,
    };
  };

  const pad = (value) => String(value).padStart(2, "0");

  const formatDateForApi = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1,
    )}-${pad(date.getDate())}`;
  };

  const getDateRange = () => {
    const today = new Date();

    const start = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const end = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    switch (dateRange) {
      case "Today":
        break;

      case "Yesterday":
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;

      case "Last 7 Days":
        start.setDate(start.getDate() - 6);
        break;

      case "Last 30 Days":
        start.setDate(start.getDate() - 29);
        break;

      case "Last 90 Days":
        start.setDate(start.getDate() - 89);
        break;

      default:
        start.setDate(start.getDate() - 29);
        break;
    }

    return {
      from_date: formatDateForApi(start),
      to_date: formatDateForApi(end),
    };
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const normalizedValue = String(value).includes("T")
      ? value
      : String(value).replace(" ", "T");

    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const getRatingColor = (rating) => {
    const value = Number(rating || 0);

    if (value >= 4.5) return "#08A878";
    if (value >= 4) return "#1677E8";
    if (value >= 3) return "#F4AD12";

    return "#F25B2A";
  };

  const getRatingBackground = (rating) => {
    const value = Number(rating || 0);

    if (value >= 4.5) return "#EAFBF4";
    if (value >= 4) return "#EDF7FF";
    if (value >= 3) return "#FFF9E7";

    return "#FFF1EA";
  };

  const getFeedbackName = (feedback) => {
    return (
      feedback?.user?.name ||
      feedback?.user_name ||
      feedback?.customer_name ||
      "Guest"
    );
  };

  const getOutletName = (feedback) => {
    return feedback?.outlet?.name || feedback?.outlet_name || "Unknown Outlet";
  };

  const getOverallRating = (feedback) => {
    return Number(feedback?.overall_experience || 0);
  };

  const getRecommendationRating = (feedback) => {
    return Number(feedback?.recommend_rating || 0);
  };

  const getSuggestion = (feedback) => {
    return feedback?.suggestions || "";
  };

  const getSource = (feedback) => {
    if (feedback?.source) {
      return feedback.source;
    }

    return feedback?.order_id ? "order" : "walk-in";
  };

  /* ---------------------------------------------------------------------- */
  /* OUTLETS                                                                */
  /* ---------------------------------------------------------------------- */

  const getOutlets = async () => {
    try {
      const response = await axiosInstance.get(`outlet`, {
        headers: getHeaders(),
      });

      if (response?.data?.status === true) {
        const outletData = response?.data?.data?.outlets || [];

        const activeOutlets = outletData?.filter(
          (outlet) =>
            outlet.status === "ACTIVE" &&
            outlet.isEventOutlet !== true &&
            outlet.isDelete !== true,
        );

        const options = [
          {
            value: "all",
            label: "All Outlets",
          },
          ...activeOutlets.map((outlet) => ({
            value: outlet._id,
            label: outlet.name || outlet.outletName || "Unknown Outlet",
          })),
        ];

        setOutlets(options);
      }
    } catch (err) {
      console.error("Failed to fetch outlets:", err);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* KPI                                                                    */
  /* ---------------------------------------------------------------------- */

  const fetchKpis = async () => {
    try {
      setKpiLoading(true);
      setError("");

      const dates = getDateRange();

      const params = {
        ...dates,
      };

      if (selectedOutletId !== "all") {
        params.outlet_id = selectedOutletId;
      }

      if (feedbackType === "order") {
        params.has_order = true;
      }

      if (feedbackType === "walk-in") {
        params.has_order = false;
      }

      if (hasSuggestions !== "all") {
        params.has_suggestions = hasSuggestions === "yes";
      }

      const response = await axiosInstance.get(`${API_BASE_URL}/analytics/kpis`, {
        params,
        headers: getHeaders(),
      });

      if (response?.data?.status === true) {
        setKpiData(response?.data?.data || null);
      } else {
        setKpiData(null);
      }
    } catch (err) {
      console.error("KPI fetch error:", err);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load feedback insights.",
      );

      setKpiData(null);
    } finally {
      setKpiLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* FEEDBACK LIST                                                          */
  /* ---------------------------------------------------------------------- */

  const fetchFeedbacks = async () => {
    try {
      setFeedbackLoading(true);

      const dates = getDateRange();

      const params = {
        ...dates,
        page: 1,
        per_page: LATEST_FEEDBACK_LIMIT,
      };

      if (selectedOutletId !== "all") {
        params.outlet_id = selectedOutletId;
      }

      if (feedbackType === "order") {
        params.has_order = true;
      }

      if (feedbackType === "walk-in") {
        params.has_order = false;
      }

      if (hasSuggestions !== "all") {
        params.has_suggestions = hasSuggestions === "yes";
      }

      const response = await axiosInstance.get(`${API_BASE_URL}/feedbacks`, {
        params,
        headers: getHeaders(),
      });

      if (response?.data?.status === true) {
        const data = response?.data?.data || {};

        setFeedbacks(data.items || []);
        setFeedbackTotal(Number(data.pagination?.total) || 0);
      } else {
        setFeedbacks([]);
        setFeedbackTotal(0);
      }
    } catch (err) {
      console.error("Feedback fetch error:", err);
      setFeedbacks([]);
      setFeedbackTotal(0);
    } finally {
      setFeedbackLoading(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* OPEN DRAWER                                                            */
  /* ---------------------------------------------------------------------- */

  const openFeedbackDetail = (feedback) => {
    const feedbackId = feedback?.id || feedback?._id;

    if (!feedbackId) return;

    setSelectedFeedbackId(feedbackId);
    setShowFeedbackDrawer(true);
  };

  const closeFeedbackDrawer = () => {
    setShowFeedbackDrawer(false);

    /*
     * Delay clearing ID until drawer animation completes.
     * Prevents content disappearing while drawer is closing.
     */
    setTimeout(() => {
      setSelectedFeedbackId(null);
    }, 300);
  };

  /* ---------------------------------------------------------------------- */
  /* EFFECTS                                                                */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    getOutlets();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchKpis();
    fetchFeedbacks();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedOutletId, feedbackType, hasSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowOutletMenu(false);
        setShowRangeMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------------------------------------------------------------- */
  /* DERIVED DATA                                                           */
  /* ---------------------------------------------------------------------- */

  const summary = kpiData?.summary || {};

  const averageScores = summary?.average_scores || {};

  const scoreDistributions = kpiData?.score_distributions || {};

  const recommendation = kpiData?.recommendation || {};

  const outletPerformance = kpiData?.outlets || [];

  const totalFeedbacks = Number(summary?.total_feedbacks) || feedbackTotal;

  const likely = Number(recommendation?.likely_to_recommend || 0);

  const neutral = Number(recommendation?.neutral || 0);

  const unlikely = Number(recommendation?.unlikely_to_recommend || 0);

  const recommendationTotal = likely + neutral + unlikely;

  const likelyPercent = recommendationTotal
    ? (likely / recommendationTotal) * 100
    : 0;

  const neutralPercent = recommendationTotal
    ? (neutral / recommendationTotal) * 100
    : 0;

  const unlikelyPercent = recommendationTotal
    ? (unlikely / recommendationTotal) * 100
    : 0;

  const ratingMetrics = useMemo(
    () => [
      {
        key: "overall_experience",
        label: "Overall Experience",
        value: averageScores?.overall_experience,
        icon: "😊",
      },
      {
        key: "menu_taste",
        label: "Taste",
        value: averageScores?.menu_taste,
        icon: "☕",
      },
      {
        key: "menu_presentation",
        label: "Presentation",
        value: averageScores?.menu_presentation,
        icon: "✨",
      },
      {
        key: "menu_freshness",
        label: "Freshness",
        value: averageScores?.menu_freshness,
        icon: "🌿",
      },
      {
        key: "menu_variety",
        label: "Menu Variety",
        value: averageScores?.menu_variety,
        icon: "🍽️",
      },
      {
        key: "menu_value_for_money",
        label: "Value for Money",
        value: averageScores?.menu_value_for_money,
        icon: "₹",
      },
      {
        key: "cleanliness",
        label: "Cleanliness",
        value: averageScores?.cleanliness,
        icon: "✦",
      },
      {
        key: "staff_friendliness",
        label: "Staff Friendliness",
        value: averageScores?.staff_friendliness,
        icon: "🤝",
      },
    ],
    [averageScores],
  );

  const strongestMetric = useMemo(() => {
    if (!ratingMetrics.length) return null;

    return [...ratingMetrics]
      .filter((item) => Number(item.value) > 0)
      .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))[0];
  }, [ratingMetrics]);

  const lowestMetric = useMemo(() => {
    if (!ratingMetrics.length) return null;

    return [...ratingMetrics]
      .filter((item) => Number(item.value) > 0)
      .sort((a, b) => Number(a.value || 0) - Number(b.value || 0))[0];
  }, [ratingMetrics]);

  const selectedOutletLabel =
    outlets.find((outlet) => outlet.value === selectedOutletId)?.label ||
    "All Outlets";

  const openAllFeedbacks = () => {
    navigate("/smart-insights/feedback-customers", {
      state: {
        outletId: selectedOutletId,
        dateRange,
        feedbackType,
        hasSuggestions,
      },
    });
  };

  return (
    <>
      <Header onSkin={setSkin} />

      <div className="main main-app feedback-insights-page">
        <div className="feedback-page-container">
          {/* HEADER */}

          <div className="feedback-page-header">
            <div>
              <Button
                variant="light"
                className="feedback-back-button"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft size={17} />
                Go Back
              </Button>

              <h1 className="feedback-page-title">
                Feedback <span>Insights</span>
              </h1>

              <p className="feedback-page-subtitle">
                Understand what customers love and where their experience can be
                improved.
              </p>
            </div>

            <div ref={filterRef} className="feedback-filter-group">
              {/* OUTLET */}

              <div className="feedback-dropdown">
                <button
                  type="button"
                  className={`feedback-filter-button ${
                    showOutletMenu ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowOutletMenu(!showOutletMenu);

                    setShowRangeMenu(false);
                  }}
                >
                  <span>
                    <Shop size={15} />
                    {selectedOutletLabel}
                  </span>

                  <ChevronDown size={16} />
                </button>

                {showOutletMenu && (
                  <div className="feedback-dropdown-menu">
                    {outlets.map((outlet) => (
                      <button
                        type="button"
                        key={outlet.value}
                        className={`feedback-dropdown-item ${
                          selectedOutletId === outlet.value ? "selected" : ""
                        }`}
                        onClick={() => {
                          setSelectedOutletId(outlet.value);

                          setShowOutletMenu(false);
                        }}
                      >
                        {outlet.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* DATE */}

              <div className="feedback-dropdown">
                <button
                  type="button"
                  className={`feedback-filter-button ${
                    showRangeMenu ? "active" : ""
                  }`}
                  onClick={() => {
                    setShowRangeMenu(!showRangeMenu);

                    setShowOutletMenu(false);
                  }}
                >
                  <span>{dateRange}</span>

                  <ChevronDown size={16} />
                </button>

                {showRangeMenu && (
                  <div className="feedback-dropdown-menu right">
                    {rangeOptions.map((option) => (
                      <button
                        type="button"
                        key={option}
                        className={`feedback-dropdown-item ${
                          dateRange === option ? "selected" : ""
                        }`}
                        onClick={() => {
                          setDateRange(option);

                          setShowRangeMenu(false);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="feedback-error">
              <ExclamationCircle size={18} />
              {error}
            </div>
          )}

          {/* COMPACT EXPERIENCE + RECOMMENDATION SUMMARY */}

          {kpiLoading ? (
            <LoadingCard text="Loading customer insights..." />
          ) : strongestMetric && lowestMetric ? (
            <Card className="feedback-insight-summary-card mb-4">
              <Card.Body>
                <div className="feedback-insight-summary">
                  <div className="feedback-insight-metrics">
                    <span className="feedback-eyebrow">At a glance</span>

                    <div className="feedback-insight-metric-row">
                      <div className="feedback-insight-metric best">
                        <span className="feedback-insight-icon">
                          <ArrowUpRight size={18} />
                        </span>
                        <div>
                          <span>Happiest with</span>
                          <strong>{strongestMetric.label}</strong>
                          <div className="feedback-insight-rating">
                            {Number(strongestMetric.value).toFixed(1)}
                            <small>/5</small>
                          </div>
                        </div>
                      </div>

                      <div className="feedback-insight-divider" />

                      <div className="feedback-insight-metric attention">
                        <span className="feedback-insight-icon">
                          <ArrowDownRight size={18} />
                        </span>
                        <div>
                          <span>Needs attention</span>
                          <strong>{lowestMetric.label}</strong>
                          <div className="feedback-insight-rating">
                            {Number(lowestMetric.value).toFixed(1)}
                            <small>/5</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="feedback-insight-section-divider" />

                  <div className="feedback-insight-recommendation">
                    <div className="feedback-insight-recommendation-top">
                      <div>
                        <span className="feedback-eyebrow">Recommendation</span>
                        <h3>Would customers recommend us?</h3>
                      </div>

                      <div className="feedback-insight-recommend-score">
                        {Math.round(likelyPercent)}
                        <small>%</small>
                      </div>
                    </div>

                    {recommendationTotal > 0 ? (
                      <>
                        <div
                          className="feedback-insight-progress"
                          aria-label="Recommendation breakdown"
                        >
                          <span
                            className="likely"
                            style={{ width: `${likelyPercent}%` }}
                          />
                          <span
                            className="neutral"
                            style={{ width: `${neutralPercent}%` }}
                          />
                          <span
                            className="unlikely"
                            style={{ width: `${unlikelyPercent}%` }}
                          />
                        </div>

                        <div className="feedback-insight-legend">
                          <div>
                            <span className="feedback-insight-dot likely" />
                            <strong>{Math.round(likelyPercent)}%</strong> Likely
                          </div>
                          <div>
                            <span className="feedback-insight-dot neutral" />
                            <strong>{Math.round(neutralPercent)}%</strong>{" "}
                            Neutral
                          </div>
                          <div>
                            <span className="feedback-insight-dot unlikely" />
                            <strong>{Math.round(unlikelyPercent)}%</strong>{" "}
                            Unlikely
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="feedback-empty-small">
                        No recommendation data available.
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : null}

          {/* RATINGS */}
          {kpiLoading ? (
            <LoadingCard text="Loading rating insights..." />
          ) : (
            <div className="experience-glance-grid mb-5">
              {ratingMetrics.map((metric, index) => (
                <RatingMetricCard
                  key={metric.key}
                  metric={metric}
                  index={index}
                  onClick={() => setSelectedRatingMetric(metric)}
                />
              ))}
            </div>
          )}

          {/* FEEDBACK LIST */}

          <div className="feedback-list-header">
            <div>
              <h2>
                Latest <span>feedback</span>
              </h2>

              <p>
                The {LATEST_FEEDBACK_LIMIT} most recent customer responses for
                the selected filters.
              </p>
            </div>

            {/* <div className="feedback-secondary-filters">
              <Form.Select
                value={feedbackType}
                onChange={(event) => setFeedbackType(event.target.value)}
              >
                <option value="all">All feedback</option>

                <option value="order">Order feedback</option>

                <option value="walk-in">Walk-in feedback</option>
              </Form.Select>

              <Form.Select
                value={hasSuggestions}
                onChange={(event) => setHasSuggestions(event.target.value)}
              >
                <option value="all">All suggestions</option>

                <option value="yes">Has suggestion</option>

                <option value="no">No suggestion</option>
              </Form.Select>
            </div> */}
          </div>

          <Card className="feedback-main-card overflow-hidden">
            <Card.Body className="p-0">
              {feedbackLoading ? (
                <div className="feedback-table-loading">
                  <Spinner animation="border" size="sm" />
                  Loading feedback...
                </div>
              ) : feedbacks.length === 0 ? (
                <div className="feedback-empty large">
                  <ChatLeftText size={30} />

                  <strong>No feedback found</strong>

                  <span>
                    Try changing your outlet, period or feedback filters.
                  </span>
                </div>
              ) : (
                <>
                  <div className="feedback-desktop-table">
                    <Table
                      responsive
                      borderless
                      hover
                      className="feedback-table mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Customer</th>

                          <th>Outlet</th>

                          <th>Experience</th>

                          <th>Recommend</th>

                          <th>Suggestion</th>

                          <th>Source</th>

                          <th>Submitted</th>

                          <th></th>
                        </tr>
                      </thead>

                      <tbody>
                        {feedbacks.map((feedback, index) => (
                          <tr
                            key={feedback.id || feedback._id || index}
                            onClick={() => openFeedbackDetail(feedback)}
                          >
                            <td>
                              <div className="feedback-customer">
                                <div className="feedback-avatar">
                                  {getFeedbackName(feedback)
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>

                                <div>
                                  <strong>{getFeedbackName(feedback)}</strong>

                                  <span>
                                    {feedback?.user?.mobile ||
                                      feedback?.user_mobile ||
                                      ""}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="feedback-outlet-name">
                                {getOutletName(feedback)}
                              </span>
                            </td>

                            <td>
                              <RatingPill value={getOverallRating(feedback)} />
                            </td>

                            <td>
                              <RatingPill
                                value={getRecommendationRating(feedback)}
                              />
                            </td>

                            <td>
                              <div className="suggestion-preview">
                                {getSuggestion(feedback) || "—"}
                              </div>
                            </td>

                            <td>
                              <SourceBadge source={getSource(feedback)} />
                            </td>

                            <td>
                              <span className="feedback-date">
                                {formatDate(feedback.submitted_at)}
                              </span>
                            </td>

                            <td>
                              <button
                                type="button"
                                className="feedback-view-button"
                                onClick={(event) => {
                                  event.stopPropagation();

                                  openFeedbackDetail(feedback);
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* MOBILE */}

                  <div className="feedback-mobile-list">
                    {feedbacks.map((feedback, index) => (
                      <div
                        key={feedback.id || feedback._id || index}
                        className="feedback-mobile-item"
                        onClick={() => openFeedbackDetail(feedback)}
                      >
                        <div className="feedback-mobile-top">
                          <div className="feedback-customer">
                            <div className="feedback-avatar">
                              {getFeedbackName(feedback)
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div>
                              <strong>{getFeedbackName(feedback)}</strong>

                              <span>{getOutletName(feedback)}</span>
                            </div>
                          </div>

                          <RatingPill value={getOverallRating(feedback)} />
                        </div>

                        {getSuggestion(feedback) && (
                          <p className="mobile-suggestion">
                            “{getSuggestion(feedback)}”
                          </p>
                        )}

                        <div className="feedback-mobile-footer">
                          <SourceBadge source={getSource(feedback)} />

                          <span>{formatDate(feedback.submitted_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="feedback-table-footer">
                    <span>
                      Showing latest <strong>{feedbacks.length}</strong> of{" "}
                      <strong>{feedbackTotal || feedbacks.length}</strong>{" "}
                      feedback responses
                    </span>

                    <button
                      type="button"
                      className="feedback-view-all-button"
                      onClick={openAllFeedbacks}
                    >
                      View all feedback
                      <ChevronRight size={13} />
                    </button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          {/* OUTLET PERFORMANCE */}

          <OutletPerformanceChart outlets={outletPerformance} />
        </div>
      </div>

      <div className="mt-4">
        <Footer />
      </div>

      {/* CUSTOMER EXPERIENCE DETAIL MODAL */}
      <RatingDetailModal
        metric={selectedRatingMetric}
        distribution={
          selectedRatingMetric
            ? scoreDistributions?.[selectedRatingMetric.key] || {}
            : {}
        }
        show={Boolean(selectedRatingMetric)}
        onHide={() => setSelectedRatingMetric(null)}
        getRatingColor={getRatingColor}
        getRatingBackground={getRatingBackground}
      />

      {/* FEEDBACK DETAIL DRAWER */}

      <FeedbackDetailDrawer
        show={showFeedbackDrawer}
        onHide={closeFeedbackDrawer}
        feedbackId={selectedFeedbackId}
        accessToken={accessToken}
        apiBaseUrl={API_BASE_URL}
      />

      <style>
        {`
                .feedback-insights-page {
                    background: #F7FBFF;
                    min-height: 100vh;
                }

                .feedback-page-container {
                    padding: 26px 28px 42px;
                }

                .feedback-page-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 24px;
                    margin-bottom: 26px;
                }

                .feedback-back-button {
                    border: 1px solid #D6E6F5 !important;
                    border-radius: 11px !important;
                    display: inline-flex !important;
                    align-items: center;
                    gap: 7px;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    margin-bottom: 18px;
                    background: #ffffff !important;
                    color: #102A43 !important;
                }

                .feedback-page-title {
                    color: #071C3A;
                    font-size: 34px;
                    line-height: 1.15;
                    font-weight: 700;
                    margin: 0 0 7px;
                }

                .feedback-page-title span,
                .feedback-section-heading h2 span,
                .feedback-list-header h2 span {
                    color: #1677E8;
                }

                .feedback-page-subtitle,
                .feedback-section-heading p,
                .feedback-list-header p {
                    color: #60748A;
                    font-size: 14px;
                    margin: 0;
                }

                .feedback-filter-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                    padding-top: 2px;
                }

                .feedback-dropdown {
                    position: relative;
                }

                .feedback-filter-button {
                    min-width: 160px;
                    height: 42px;
                    padding: 0 14px;
                    border: 1px solid #D6E6F5;
                    border-radius: 11px;
                    background: #ffffff;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 18px;
                }

                .feedback-filter-button > span {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }

                .feedback-filter-button.active {
                    border-color: #4C9BE8;
                    box-shadow: 0 0 0 2px rgba(22,119,232,.1);
                }

                .feedback-dropdown-menu {
                    position: absolute;
                    left: 0;
                    top: calc(100% + 7px);
                    width: 220px;
                    max-height: 300px;
                    overflow-y: auto;
                    padding: 6px;
                    background: #ffffff;
                    border: 1px solid #DDEEFF;
                    border-radius: 12px;
                    box-shadow: 0 16px 40px rgba(37,112,180,.14);
                    z-index: 1100;
                }

                .feedback-dropdown-menu.right {
                    left: auto;
                    right: 0;
                }

                .feedback-dropdown-item {
                    width: 100%;
                    border: 0;
                    background: transparent;
                    text-align: left;
                    padding: 9px 11px;
                    border-radius: 8px;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 600;
                }

                .feedback-dropdown-item:hover,
                .feedback-dropdown-item.selected {
                    background: #EDF7FF;
                    color: #1677E8;
                }

                .feedback-error {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    background: #FFF1EA;
                    color: #F25B2A;
                    border: 1px solid #FFE0D2;
                    border-radius: 12px;
                    padding: 12px 15px;
                    margin-bottom: 18px;
                    font-size: 13px;
                    font-weight: 700;
                }

                .feedback-summary-card {
                    height: 100%;
                    border: 1px solid #DDEEFF !important;
                    border-radius: 15px !important;
                    box-shadow: none !important;
                    overflow: hidden;
                }

                .feedback-summary-card .card-body {
                    padding: 18px 19px;
                }

                .feedback-summary-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                }

                .feedback-summary-label {
                    display: block;
                    color: #60748A;
                    font-size: 12px;
                    font-weight: 700;
                    margin-bottom: 7px;
                }

                .feedback-summary-value {
                    color: #071C3A;
                    font-size: 28px;
                    font-weight: 800;
                    line-height: 1;
                }

                .feedback-summary-value small {
                    color: #71869A;
                    font-size: 12px;
                    font-weight: 600;
                    margin-left: 5px;
                }

                .feedback-summary-icon {
                    width: 42px;
                    height: 42px;
                    flex: 0 0 42px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .feedback-summary-card.green .feedback-summary-icon {
                    background: #EAFBF4;
                    color: #08A878;
                }

                .feedback-summary-card.yellow .feedback-summary-icon {
                    background: #FFF9E7;
                    color: #F4AD12;
                }

                .feedback-summary-card.purple .feedback-summary-icon {
                    background: #EAF5FF;
                    color: #1677E8;
                }

                .feedback-summary-card.blue .feedback-summary-icon {
                    background: #EDF7FF;
                    color: #1677E8;
                }

                /* COMPACT EXPERIENCE + RECOMMENDATION SUMMARY */
                .feedback-insight-summary-card {
                    border: 1px solid #C9E4FF !important;
                    border-radius: 16px !important;
                    box-shadow: 0 10px 28px rgba(37,112,180,.10) !important;
                    background: #ffffff;
                    overflow: hidden;
                }

                .feedback-insight-summary-card .card-body {
                    padding: 18px 22px;
                }

                .feedback-insight-summary {
                    display: grid;
                    grid-template-columns: minmax(0, 1.1fr) 1px minmax(360px, .9fr);
                    align-items: center;
                    gap: 24px;
                }

                .feedback-insight-metric-row {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
                    align-items: center;
                    gap: 20px;
                    margin-top: 9px;
                }

                .feedback-insight-metric {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }

                .feedback-insight-icon {
                    width: 42px;
                    height: 42px;
                    flex: 0 0 42px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .feedback-insight-metric.best .feedback-insight-icon {
                    color: #08A878;
                    background: #EAFBF4;
                }

                .feedback-insight-metric.attention .feedback-insight-icon {
                    color: #F25B2A;
                    background: #FFF1EA;
                }

                .feedback-insight-metric > div { min-width: 0; }

                .feedback-insight-metric span:not(.feedback-insight-icon) {
                    display: block;
                    color: #60748A;
                    font-size: 10px;
                    font-weight: 700;
                    margin-bottom: 1px;
                }

                .feedback-insight-metric strong {
                    display: block;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 800;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .feedback-insight-metric.best strong { color: #08A878; }

                .feedback-insight-rating {
                    color: #071C3A;
                    font-size: 19px;
                    line-height: 1.1;
                    font-weight: 800;
                    margin-top: 3px;
                }

                .feedback-insight-rating small {
                    color: #71869A;
                    font-size: 10px;
                    font-weight: 700;
                    margin-left: 2px;
                }

                .feedback-insight-divider,
                .feedback-insight-section-divider {
                    width: 1px;
                    align-self: stretch;
                    background: #EAF2FB;
                }

                .feedback-insight-section-divider { min-height: 88px; }

                .feedback-insight-recommendation { min-width: 0; }

                .feedback-insight-recommendation-top {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 18px;
                    margin-bottom: 11px;
                }

                .feedback-insight-recommendation h3 {
                    color: #102A43;
                    font-size: 14px;
                    font-weight: 800;
                    margin: 3px 0 0;
                }

                .feedback-insight-recommend-score {
                    color: #08A878;
                    font-size: 27px;
                    line-height: 1;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .feedback-insight-recommend-score small {
                    font-size: 12px;
                    margin-left: 1px;
                }

                .feedback-insight-progress {
                    width: 100%;
                    height: 10px;
                    display: flex;
                    overflow: hidden;
                    border-radius: 99px;
                    background: #EAF2FB;
                }

                .feedback-insight-progress > span {
                    display: block;
                    height: 100%;
                }

                .feedback-insight-progress .likely,
                .feedback-insight-dot.likely { background: #5CCFA4; }
                .feedback-insight-progress .neutral,
                .feedback-insight-dot.neutral { background: #F7C948; }
                .feedback-insight-progress .unlikely,
                .feedback-insight-dot.unlikely { background: #F25B2A; }

                .feedback-insight-legend {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    flex-wrap: wrap;
                    margin-top: 9px;
                    color: #60748A;
                    font-size: 10px;
                    font-weight: 600;
                }

                .feedback-insight-legend > div {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    white-space: nowrap;
                }

                .feedback-insight-legend strong {
                    color: #102A43;
                    font-weight: 800;
                }

                .feedback-insight-dot {
                    width: 8px;
                    height: 8px;
                    flex: 0 0 8px;
                    border-radius: 50%;
                }

                .feedback-highlight-card {
                    border: 1px solid #DDEEFF !important;
                    border-radius: 16px !important;
                    background: linear-gradient(
                        100deg,
                        #F4FAFF 0%,
                        #ffffff 65%
                    );
                    box-shadow: none !important;
                }

                .feedback-highlight-card .card-body {
                    padding: 21px 24px;
                }

                .feedback-highlight-grid {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 30px;
                }

                .feedback-highlight-main h3 {
                    color: #071C3A;
                    font-size: 19px;
                    font-weight: 600;
                    margin: 4px 0 6px;
                }

                .feedback-highlight-main h3 strong {
                    color: #1677E8;
                }

                .feedback-highlight-main p {
                    color: #60748A;
                    font-size: 13px;
                    margin: 0;
                }

                .feedback-highlight-stat {
                    min-width: 200px;
                    border-left: 1px solid #DDEEFF;
                    padding-left: 24px;
                }

                .feedback-highlight-stat > span {
                    display: block;
                    color: #71869A;
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: .7px;
                    font-weight: 800;
                    margin-bottom: 3px;
                }

                .feedback-highlight-stat > strong {
                    display: block;
                    color: #102A43;
                    font-size: 13px;
                    margin-bottom: 5px;
                }

                .feedback-highlight-stat > div {
                    color: #1677E8;
                    font-size: 25px;
                    line-height: 1;
                    font-weight: 800;
                }

                .feedback-highlight-stat small {
                    color: #71869A;
                    font-size: 11px;
                    margin-left: 2px;
                }

                .feedback-eyebrow {
                    color: #1677E8;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    font-weight: 800;
                }

                .feedback-section-heading,
                .feedback-list-header {
                    display: flex;
                    align-items: flex-end;
                    justify-content: space-between;
                    gap: 20px;
                    margin-bottom: 17px;
                }

                .feedback-section-heading h2,
                .feedback-list-header h2 {
                    color: #071C3A;
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0 0 4px;
                }

                .rating-metric-card {
                    height: 100%;
                    border: 1px solid #DDEEFF !important;
                    border-radius: 15px !important;
                    box-shadow: none !important;
                    transition: transform .2s ease, box-shadow .2s ease;
                }

                .rating-metric-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 9px 25px rgba(37,112,180,.08) !important;
                }

                .rating-metric-card .card-body {
                    padding: 17px 18px;
                }

                .rating-card-top {
                    display: flex;
                    justify-content: space-between;
                    gap: 15px;
                    margin-bottom: 14px;
                }

                .rating-card-label {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 700;
                }

                .rating-card-icon {
                    font-size: 15px;
                }

                .rating-score {
                    min-width: 62px;
                    height: 32px;
                    padding: 0 9px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                    font-size: 16px;
                    font-weight: 800;
                }

                .rating-score svg {
                    color: #FFB51B;
                }

                .rating-distribution {
                    display: flex;
                    align-items: flex-end;
                    gap: 5px;
                    height: 44px;
                }

                .rating-distribution-column {
                    flex: 1;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    align-items: center;
                    gap: 3px;
                }

                .rating-distribution-count {
                    color: #71869A;
                    font-size: 9px;
                    font-weight: 700;
                }

                .rating-distribution-bar-wrap {
                    width: 100%;
                    height: 25px;
                    display: flex;
                    align-items: flex-end;
                }

                .rating-distribution-bar {
                    width: 100%;
                    min-height: 2px;
                    background: #7FB8F0;
                    border-radius: 3px 3px 1px 1px;
                }

                .rating-distribution-star {
                    color: #8AA0B5;
                    font-size: 9px;
                    font-weight: 700;
                }

                .feedback-main-card {
                    border: 1px solid #C9E4FF !important;
                    border-radius: 16px !important;
                    background: #ffffff !important;
                    box-shadow: 0 10px 28px rgba(37,112,180,.10) !important;
                }

                .feedback-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 15px;
                }

                .feedback-card-header h3 {
                    color: #071C3A;
                    font-size: 18px;
                    font-weight: 700;
                    margin: 4px 0 0;
                }

                .feedback-muted {
                    color: #60748A;
                    font-size: 12px;
                }

                .recommend-score {
                    color: #08A878;
                    font-size: 28px;
                    line-height: 1;
                    font-weight: 800;
                }

                .recommend-score small {
                    font-size: 12px;
                    margin-left: 1px;
                }

                .recommend-row {
                    margin-bottom: 18px;
                }

                .recommend-row-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 10px;
                    margin-bottom: 7px;
                }

                .recommend-row-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #334E68;
                    font-size: 12px;
                    font-weight: 700;
                }

                .recommend-row-value {
                    color: #102A43;
                    font-size: 12px;
                    font-weight: 800;
                }

                .recommend-row .progress {
                    height: 7px;
                    background: #EAF2FB;
                    border-radius: 99px;
                }

                .recommend-row.positive .progress-bar {
                    background: #5CCFA4;
                }

                .recommend-row.positive .recommend-row-label svg {
                    color: #08A878;
                }

                .recommend-row.neutral .progress-bar {
                    background: #F7C948;
                }

                .recommend-row.neutral .recommend-row-label svg {
                    color: #F4AD12;
                }

                .recommend-row.negative .progress-bar {
                    background: #F7936B;
                }

                .recommend-row.negative .recommend-row-label svg {
                    color: #F25B2A;
                }

                .outlet-chart-section {
                    margin-top: 42px;
                }

                .outlet-metric-toggle {
                    display: inline-flex;
                    padding: 3px;
                    border: 1px solid #C9E4FF;
                    border-radius: 10px;
                    background: #ffffff;
                    box-shadow: 0 6px 16px rgba(37,112,180,.08);
                }

                .outlet-metric-toggle button {
                    border: 0;
                    background: transparent;
                    color: #486581;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 6px 12px;
                    border-radius: 7px;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }

                .outlet-metric-toggle button.active {
                    background: #DCEEFF;
                    color: #1677E8;
                }

                .outlet-metric-toggle button svg {
                    opacity: .7;
                }

                .feedback-metric-tooltip .tooltip-inner {
                    max-width: 240px;
                    padding: 8px 11px;
                    background: #071C3A;
                    color: #ffffff;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 1.45;
                    text-align: left;
                    border-radius: 8px;
                }

                .feedback-metric-tooltip.bs-tooltip-top .tooltip-arrow::before {
                    border-top-color: #071C3A;
                }

                .outlet-bar-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .outlet-bar-row {
                    display: grid;
                    grid-template-columns: 180px 1fr;
                    align-items: center;
                    gap: 16px;
                }

                .outlet-bar-name {
                    color: #102A43;
                    font-size: 13px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .outlet-bar-track {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }

                .outlet-bar-fill {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    height: 24px;
                    border-radius: 4px;
                    padding: 0 12px;
                    flex-shrink: 0;
                    transition: width 0.4s ease;
                }

                .outlet-bar-value {
                    color: #102A43;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                }

                .outlet-bar-value.inside {
                    color: #ffffff;
                }

                .outlet-show-more {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    margin-top: 22px;
                    border: 0;
                    background: transparent;
                    color: #102A43;
                    font-size: 13px;
                    font-weight: 600;
                    padding: 0;
                }

                .feedback-secondary-filters {
                    display: flex;
                    gap: 8px;
                }

                .feedback-secondary-filters .form-select {
                    width: auto;
                    min-width: 150px;
                    height: 38px;
                    border-color: #D6E6F5;
                    border-radius: 10px;
                    color: #334E68;
                    font-size: 12px;
                    font-weight: 600;
                    box-shadow: none;
                }

                .feedback-table {
                    vertical-align: middle;
                }

                .feedback-table thead th {
                    background: #F4FAFF;
                    color: #60748A;
                    border-bottom: 1px solid #EAF2FB;
                    padding: 13px 15px;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: .4px;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .feedback-table tbody tr {
                    cursor: pointer;
                    border-bottom: 1px solid #EAF2FB;
                    transition: background .15s ease;
                }

                .feedback-table tbody tr:hover {
                    background: #F7FBFF;
                }

                .feedback-table tbody td {
                    padding: 13px 15px;
                    color: #334E68;
                    font-size: 12px;
                }

                .feedback-customer {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .feedback-avatar {
                    width: 32px;
                    height: 32px;
                    flex: 0 0 32px;
                    border-radius: 50%;
                    background: #DCEEFF;
                    color: #1677E8;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 800;
                }

                .feedback-customer strong {
                    display: block;
                    color: #102A43;
                    font-size: 12px;
                    white-space: nowrap;
                }

                .feedback-customer span {
                    display: block;
                    color: #8AA0B5;
                    font-size: 10px;
                    margin-top: 1px;
                }

                .feedback-outlet-name {
                    color: #334E68;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .rating-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    border-radius: 7px;
                    padding: 5px 7px;
                    font-size: 11px;
                    font-weight: 800;
                }

                .rating-pill svg {
                    color: #FFB51B;
                }

                .suggestion-preview {
                    max-width: 230px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    color: #60748A;
                }

                .source-badge {
                    display: inline-flex;
                    padding: 5px 8px;
                    border-radius: 7px;
                    background: #EDF7FF;
                    color: #1677E8;
                    font-size: 9px;
                    text-transform: capitalize;
                    font-weight: 800;
                }

                .source-badge.walk-in {
                    background: #EAFBF4;
                    color: #08A878;
                }

                .feedback-date {
                    white-space: nowrap;
                    color: #60748A;
                    font-size: 11px;
                }

                .feedback-view-button {
                    border: 0;
                    border-radius: 7px;
                    background: #EDF7FF;
                    color: #1677E8;
                    padding: 6px 10px;
                    font-size: 10px;
                    font-weight: 800;
                }

                .feedback-table-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    padding: 13px 16px;
                    background: #ffffff;
                    color: #71869A;
                    font-size: 11px;
                }

                .feedback-view-all-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    border: 1px solid #DDEEFF;
                    border-radius: 9px;
                    background: #EDF7FF;
                    color: #1677E8;
                    padding: 7px 13px;
                    font-size: 12px;
                    font-weight: 700;
                    transition: background .15s ease;
                }

                .feedback-view-all-button:hover {
                    background: #DCEEFF;
                }

                .feedback-table-loading {
                    min-height: 180px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    color: #60748A;
                    font-size: 13px;
                    font-weight: 600;
                }

                .feedback-empty {
                    min-height: 120px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #71869A;
                    font-size: 12px;
                    text-align: center;
                }

                .feedback-empty.large {
                    min-height: 230px;
                    flex-direction: column;
                    gap: 7px;
                }

                .feedback-empty.large strong {
                    color: #334E68;
                    font-size: 14px;
                }

                .feedback-empty-small {
                    color: #71869A;
                    font-size: 11px;
                    padding-top: 5px;
                }

                .feedback-mobile-list {
                    display: none;
                }

                .feedback-loading-card {
                    height: 160px;
                    border: 1px solid #C9E4FF;
                    border-radius: 15px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    color: #60748A;
                    font-size: 13px;
                    margin-bottom: 35px;
                    background: #ffffff;
                    box-shadow: 0 10px 28px rgba(37,112,180,.10);
                }


                /* CUSTOMER EXPERIENCE AT A GLANCE */
                .experience-glance-heading {
                    align-items: center;
                    margin-bottom: 14px;
                }
                .experience-view-details {
                    border: 0;
                    background: transparent;
                    color: #1677E8;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 6px 2px;
                    font-size: 12px;
                    font-weight: 700;
                    white-space: nowrap;
                }
                .experience-view-details span { font-size: 17px; line-height: 1; }
                .experience-glance-grid {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 10px;
                }
                .experience-glance-card {
                    width: 100%;
                    min-height: 80px;
                    border: 1px solid #C9E4FF;
                    border-left: 3px solid #1677E8;
                    border-radius: 13px;
                    padding: 12px 13px 12px 15px;
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    text-align: left;
                    cursor: pointer;
                    background: #ffffff;
                    box-shadow: 0 8px 22px rgba(37,112,180,.10);
                    transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
                }
                .experience-glance-card:hover {
                    transform: translateY(-2px);
                    border-color: #1677E8;
                    box-shadow: 0 14px 28px rgba(37,112,180,.16);
                }
                .experience-glance-icon {
                    width: 47px;
                    height: 47px;
                    flex: 0 0 47px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    line-height: 1;
                }
                .experience-glance-content {
                    min-width: 0;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                }
                .experience-glance-score {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    color: #071C3A;
                    font-size: 17px;
                    font-weight: 800;
                    line-height: 1.1;
                }
                .experience-glance-score > svg:first-of-type { color: #FFB51B; }
                .experience-info-icon {
                    color: #71869A !important;
                    margin-left: auto;
                    margin-right: 2px;
                }
                .experience-glance-label {
                    color: #102A43;
                    font-size: 11px;
                    font-weight: 600;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .experience-glance-arrow { flex: 0 0 auto; }

                /* RATING DETAIL MODAL */
                .rating-detail-modal .modal-dialog { max-width: 560px; }
                .rating-detail-modal .modal-content {
                    border: 0;
                    border-radius: 18px;
                    box-shadow: 0 24px 70px rgba(7,28,58,.18);
                    overflow: hidden;
                }
                .rating-detail-modal .modal-header {
                    border-bottom: 1px solid #EAF2FB;
                    padding: 18px 20px;
                }
                .rating-detail-title-wrap {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .rating-detail-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: #EAF5FF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 22px;
                }
                .rating-detail-modal .modal-title {
                    color: #071C3A;
                    font-size: 19px;
                    font-weight: 800;
                    margin-top: 2px;
                }
                .rating-detail-modal .modal-body { padding: 20px; }
                .rating-detail-summary {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    padding: 14px;
                    border: 1px solid #EAF2FB;
                    border-radius: 13px;
                    background: #F7FBFF;
                    margin-bottom: 22px;
                }
                .rating-detail-score {
                    min-width: 74px;
                    height: 44px;
                    padding: 0 12px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    font-size: 21px;
                    font-weight: 800;
                }
                .rating-detail-score svg { color: #FFB51B; }
                .rating-detail-summary strong,
                .rating-detail-summary span { display: block; }
                .rating-detail-summary strong { color: #102A43; font-size: 13px; }
                .rating-detail-summary span {
                    color: #60748A;
                    font-size: 11px;
                    margin-top: 2px;
                }
                .rating-detail-distribution {
                    display: flex;
                    flex-direction: column;
                    gap: 13px;
                }
                .rating-detail-row {
                    display: grid;
                    grid-template-columns: 38px 1fr 66px;
                    align-items: center;
                    gap: 10px;
                }
                .rating-detail-star-label {
                    display: flex;
                    align-items: center;
                    gap: 3px;
                    color: #334E68;
                    font-size: 12px;
                    font-weight: 700;
                }
                .rating-detail-star-label svg { color: #FFB51B; }
                .rating-detail-track {
                    height: 9px;
                    border-radius: 99px;
                    background: #EAF2FB;
                    overflow: hidden;
                }
                .rating-detail-fill {
                    height: 100%;
                    border-radius: inherit;
                    background: #1677E8;
                }
                .rating-detail-count {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 6px;
                    font-size: 11px;
                }
                .rating-detail-count strong { color: #102A43; }
                .rating-detail-count span {
                    min-width: 30px;
                    color: #71869A;
                    text-align: right;
                }

                @media (max-width: 1199px) {
                    .feedback-page-header {
                        flex-direction: column;
                    }

                    .feedback-filter-group {
                        width: 100%;
                    }
                }

                @media (max-width: 991px) {
                    .feedback-insight-summary {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .feedback-insight-section-divider {
                        width: 100%;
                        height: 1px;
                        min-height: 1px;
                    }

                    .experience-glance-grid {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .outlet-bar-row {
                        grid-template-columns: 130px 1fr;
                    }
                }

                @media (max-width: 767px) {
                    .feedback-insight-summary-card .card-body {
                        padding: 16px;
                    }

                    .feedback-insight-metric-row {
                        grid-template-columns: 1fr 1fr;
                        gap: 14px;
                    }

                    .feedback-insight-divider { display: none; }

                    .feedback-insight-metric {
                        align-items: flex-start;
                    }

                    .feedback-insight-icon {
                        width: 36px;
                        height: 36px;
                        flex-basis: 36px;
                    }

                    .feedback-page-container {
                        padding: 18px 14px 30px;
                    }

                    .feedback-page-title {
                        font-size: 27px;
                    }

                    .feedback-page-subtitle {
                        font-size: 12px;
                    }

                    .feedback-filter-group {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                    }

                    .feedback-filter-button {
                        min-width: 0;
                        width: 100%;
                    }

                    .feedback-highlight-grid {
                        align-items: flex-start;
                        flex-direction: column;
                        gap: 15px;
                    }

                    .feedback-highlight-stat {
                        width: 100%;
                        border-left: 0;
                        border-top: 1px solid #DDEEFF;
                        padding-left: 0;
                        padding-top: 13px;
                    }

                    .feedback-section-heading,
                    .feedback-list-header {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .feedback-section-heading h2,
                    .feedback-list-header h2 {
                        font-size: 21px;
                    }

                    .feedback-secondary-filters {
                        width: 100%;
                    }

                    .feedback-secondary-filters
                        .form-select {
                        flex: 1;
                        min-width: 0;
                    }

                    .feedback-desktop-table {
                        display: none;
                    }

                    .feedback-mobile-list {
                        display: block;
                    }

                    .feedback-mobile-item {
                        padding: 15px;
                        border-bottom: 1px solid #EAF2FB;
                        cursor: pointer;
                    }

                    .feedback-mobile-item:last-child {
                        border-bottom: 0;
                    }

                    .feedback-mobile-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        gap: 10px;
                    }

                    .mobile-suggestion {
                        color: #486581;
                        font-size: 11px;
                        line-height: 1.55;
                        margin: 12px 0;
                        padding: 9px 11px;
                        border-radius: 8px;
                        background: #F7FBFF;
                    }

                    .feedback-mobile-footer {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        color: #71869A;
                        font-size: 10px;
                        margin-top: 11px;
                    }

                    .feedback-table-footer {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                }

                @media (max-width: 480px) {
                    .feedback-insight-metric-row {
                        grid-template-columns: 1fr;
                    }

                    .feedback-insight-recommendation-top {
                        align-items: flex-start;
                    }

                    .feedback-insight-legend {
                        gap: 10px 14px;
                    }

                    .experience-glance-grid {
                        grid-template-columns: 1fr;
                    }

                    .feedback-filter-group {
                        grid-template-columns: 1fr;
                    }

                    .feedback-dropdown-menu {
                        width: 100%;
                    }

                    .feedback-secondary-filters {
                        flex-direction: column;
                    }

                    .feedback-secondary-filters
                        .form-select {
                        width: 100%;
                    }

                    .outlet-bar-row {
                        grid-template-columns: 1fr;
                        gap: 6px;
                    }
                }
                `}
      </style>
    </>
  );
};

/* ========================================================================== */
/* RATING METRIC CARD                                                         */
/* ========================================================================== */

const RATING_CARD_THEMES = [
  { card: "#F4FAFF", bubble: "#DCEEFF", accent: "#1677E8" },
  { card: "#FFF9E7", bubble: "#FFF4CC", accent: "#F4AD12" },
  { card: "#EAFBF4", bubble: "#D8F6EA", accent: "#08A878" },
  { card: "#FFF1EA", bubble: "#FFE0D2", accent: "#F25B2A" },
  { card: "#F4FAFF", bubble: "#DCEEFF", accent: "#1677E8" },
  { card: "#FFF9E7", bubble: "#FFF4CC", accent: "#F4AD12" },
  { card: "#EAFBF4", bubble: "#D8F6EA", accent: "#08A878" },
  { card: "#F4FAFF", bubble: "#DCEEFF", accent: "#1677E8" },
];

const RatingMetricCard = ({ metric, index, onClick }) => {
  const score = Number(metric?.value || 0);
  const theme = RATING_CARD_THEMES[index % RATING_CARD_THEMES.length];

  return (
    <button
      type="button"
      className="experience-glance-card"
      style={{
        background: `linear-gradient(135deg, ${theme.card} 0%, #ffffff 62%)`,
        borderLeftColor: theme.accent,
      }}
      onClick={onClick}
      aria-label={`View ${metric.label} rating details`}
    >
      <span
        className="experience-glance-icon"
        style={{ background: theme.bubble, color: theme.accent }}
      >
        {metric.icon}
      </span>

      <span className="experience-glance-content">
        <span className="experience-glance-score">
          {score.toFixed(1)}
          <StarFill size={13} />
          <InfoCircle size={12} className="experience-info-icon" />
        </span>
        <span className="experience-glance-label">{metric.label}</span>
      </span>

      <ChevronRight
        size={17}
        className="experience-glance-arrow"
        style={{ color: theme.accent }}
      />
    </button>
  );
};

/* ========================================================================== */
/* RATING DETAIL MODAL                                                        */
/* ========================================================================== */

const RatingDetailModal = ({
  metric,
  distribution,
  show,
  onHide,
  getRatingColor,
  getRatingBackground,
}) => {
  if (!metric) return null;

  const score = Number(metric?.value || 0);
  const values = [1, 2, 3, 4, 5].map((rating) =>
    Number(distribution?.[rating] || 0),
  );
  const total = values.reduce((sum, value) => sum + value, 0);

  return (
    <Modal show={show} onHide={onHide} centered className="rating-detail-modal">
      <Modal.Header closeButton>
        <div className="rating-detail-title-wrap">
          <span className="rating-detail-icon">{metric.icon}</span>
          <div>
            <span className="feedback-eyebrow">Customer experience</span>
            <Modal.Title>{metric.label}</Modal.Title>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="rating-detail-summary">
          <div
            className="rating-detail-score"
            style={{
              color: getRatingColor(score),
              background: getRatingBackground(score),
            }}
          >
            {score.toFixed(1)}
            <StarFill size={15} />
          </div>
          <div>
            <strong>Average rating</strong>
            <span>
              Based on {total} response{total === 1 ? "" : "s"} in the selected
              period.
            </span>
          </div>
        </div>

        <div className="rating-detail-distribution">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = Number(distribution?.[rating] || 0);
            const percent = total ? (count / total) * 100 : 0;

            return (
              <div className="rating-detail-row" key={rating}>
                <div className="rating-detail-star-label">
                  {rating} <StarFill size={10} />
                </div>
                <div className="rating-detail-track">
                  <div
                    className="rating-detail-fill"
                    style={{
                      width: `${percent}%`,
                      opacity: 0.48 + rating * 0.1,
                    }}
                  />
                </div>
                <div className="rating-detail-count">
                  <strong>{count}</strong>
                  <span>{Math.round(percent)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
};

/* RECOMMENDATION ROW                                                         */
/* ========================================================================== */

const RecommendationRow = ({ icon, label, value, percent, className }) => {
  return (
    <div className={`recommend-row ${className}`}>
      <div className="recommend-row-header">
        <div className="recommend-row-label">
          {icon}
          {label}
        </div>

        <div className="recommend-row-value">
          {value} · {Math.round(percent)}%
        </div>
      </div>

      <ProgressBar now={percent} />
    </div>
  );
};

/* ========================================================================== */
/* OUTLET PERFORMANCE CHART                                                   */
/* ========================================================================== */

const OUTLET_METRICS = [
  {
    key: "avg_overall_experience",
    label: "Experience",
    max: 5,
    format: (value) => `${value.toFixed(1)} / 5`,
    tooltip:
      "Average overall experience score (1–5) given by customers at this outlet.",
  },
  {
    key: "avg_recommend_rating",
    label: "Recommend",
    max: 5,
    format: (value) => `${value.toFixed(1)} / 5`,
    tooltip:
      "Average rating for how likely customers are to recommend this outlet (1–5).",
  },
  {
    key: "total_feedbacks",
    label: "Responses",
    format: (value) => `${value} response${value === 1 ? "" : "s"}`,
    tooltip: "Total number of feedback submissions received from this outlet.",
  },
];

const OUTLET_VISIBLE_COUNT = 8;

const getOutletBarColor = (rank) => {
  if (rank === 0) return "#1677E8";
  if (rank === 1) return "#4C9BE8";
  if (rank <= 3) return "#7FB8F0";
  return "#A7D2F8";
};

const OutletPerformanceChart = ({ outlets }) => {
  const [metricKey, setMetricKey] = useState(OUTLET_METRICS[0].key);
  const [showAll, setShowAll] = useState(false);

  const metric = OUTLET_METRICS.find((item) => item.key === metricKey);

  const rows = useMemo(
    () =>
      (outlets || [])
        .map((outlet, index) => ({
          id: `${outlet?.outlet_id || "outlet"}-${index}`,
          name: outlet?.outlet_name || "Unknown Outlet",
          value: Number(outlet?.[metricKey] || 0),
        }))
        .sort((a, b) => b.value - a.value),
    [outlets, metricKey],
  );

  const scaleMax = metric.max || Math.max(...rows.map((row) => row.value), 1);

  const visibleRows = showAll ? rows : rows.slice(0, OUTLET_VISIBLE_COUNT);

  return (
    <div className="outlet-chart-section">
      <div className="feedback-list-header">
        <div>
          <h2>
            Outlet <span>performance</span>
          </h2>

          <p>Compare how each outlet is performing for the selected period.</p>
        </div>

        <div className="outlet-metric-toggle">
          {OUTLET_METRICS.map((item) => (
            <OverlayTrigger
              key={item.key}
              placement="top"
              delay={{ show: 180, hide: 80 }}
              overlay={
                <Tooltip
                  id={`outlet-metric-${item.key}`}
                  className="feedback-metric-tooltip"
                >
                  {item.tooltip}
                </Tooltip>
              }
            >
              <button
                type="button"
                className={item.key === metricKey ? "active" : ""}
                onClick={() => setMetricKey(item.key)}
                aria-label={`${item.label}. ${item.tooltip}`}
              >
                {item.label}
                <InfoCircle size={11} />
              </button>
            </OverlayTrigger>
          ))}
        </div>
      </div>

      <Card className="feedback-main-card">
        <Card.Body className="p-4">
          {rows.length === 0 ? (
            <div className="feedback-empty">
              <GeoAlt size={22} />
              No outlet performance data available for this period.
            </div>
          ) : (
            <>
              <div className="outlet-bar-list">
                {visibleRows.map((row, rank) => {
                  const percent = Math.min(
                    100,
                    Math.max(2, (row.value / scaleMax) * 100),
                  );

                  const labelInside = percent >= 85;

                  return (
                    <div className="outlet-bar-row" key={row.id}>
                      <div className="outlet-bar-name" title={row.name}>
                        {row.name}
                      </div>

                      <div className="outlet-bar-track">
                        <div
                          className="outlet-bar-fill"
                          style={{
                            width: `${percent}%`,
                            background: getOutletBarColor(rank),
                          }}
                        >
                          {labelInside && (
                            <span className="outlet-bar-value inside">
                              {metric.format(row.value)}
                            </span>
                          )}
                        </div>

                        {!labelInside && (
                          <span className="outlet-bar-value">
                            {metric.format(row.value)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {rows.length > OUTLET_VISIBLE_COUNT && (
                <button
                  type="button"
                  className="outlet-show-more"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? "Show less" : "Show more"}

                  <ChevronDown
                    size={12}
                    style={{
                      transform: showAll ? "rotate(180deg)" : "none",
                    }}
                  />
                </button>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

/* ========================================================================== */
/* RATING PILL                                                                */
/* ========================================================================== */

export const RatingPill = ({ value }) => {
  const rating = Number(value || 0);

  let background = "#FFF1EA";
  let color = "#F25B2A";

  if (rating >= 4.5) {
    background = "#EAFBF4";
    color = "#08A878";
  } else if (rating >= 4) {
    background = "#EDF7FF";
    color = "#1677E8";
  } else if (rating >= 3) {
    background = "#FFF9E7";
    color = "#B7791F";
  }

  return (
    <span
      className="rating-pill"
      style={{
        background,
        color,
      }}
    >
      <StarFill size={9} />

      {rating ? rating.toFixed(1) : "—"}
    </span>
  );
};

/* ========================================================================== */
/* SOURCE BADGE                                                               */
/* ========================================================================== */

export const SourceBadge = ({ source }) => {
  const normalizedSource = String(source || "walk-in").toLowerCase();

  const isWalkIn = ["walk-in", "walkin", "outlet"].includes(normalizedSource);

  return (
    <span className={`source-badge ${isWalkIn ? "walk-in" : ""}`}>
      {isWalkIn ? "Walk-in" : "Order"}
    </span>
  );
};

const LoadingCard = ({ text }) => {
  return (
    <div className="feedback-loading-card">
      <Spinner animation="border" size="sm" />

      {text}
    </div>
  );
};

export default FeedbackListing;
